/**
 * Readable foreground selection for arbitrary solid fills. Pure value module
 * (no React/RN imports), consistent with the rest of `shared/theme`.
 *
 * WHY THIS EXISTS
 * Status accents do not keep their luminance across themes. `semantic.status.
 * success` is `#4CAF50` in light but `#66CE66` in dark; `warning` is `#E06A20`
 * light and `#FF7629` dark. Both dark variants are *lighter* than their light
 * counterparts, so a hardcoded `#FFFFFF` label — which reads fine in light —
 * drops to roughly 2.0:1 on the dark-theme fill and becomes unreadable.
 *
 * Picking the foreground from the fill removes the whole class of bug: callers
 * never restate a foreground color, so the two can never drift apart.
 */

/** Ink for light fills. Matches `text.primary` in the light theme. */
export const ON_LIGHT_INK = '#1A1A1A';

/** Ink for dark fills. */
export const ON_DARK_INK = '#FFFFFF';

type Rgb = { r: number; g: number; b: number };

function parseColor(color: string): Rgb | null {
  const value = color.trim();

  if (value.startsWith('#')) {
    let hex = value.slice(1);
    // Expand shorthand (#abc -> #aabbcc) before parsing.
    if (hex.length === 3 || hex.length === 4) {
      hex = hex
        .split('')
        .map(ch => ch + ch)
        .join('');
    }
    if (hex.length !== 6 && hex.length !== 8) return null;
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return null;
    return { r, g, b };
  }

  const match = value.match(/^rgba?\(([^)]+)\)$/i);
  if (match) {
    const parts = match[1].split(',').map(p => parseFloat(p.trim()));
    if (parts.length < 3 || parts.some(Number.isNaN)) return null;
    return { r: parts[0], g: parts[1], b: parts[2] };
  }

  return null;
}

function channelLuminance(channel8Bit: number): number {
  const c = channel8Bit / 255;
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

/** WCAG 2.1 relative luminance. */
export function relativeLuminance(color: string): number {
  const rgb = parseColor(color);
  if (!rgb) return 0;
  return (
    0.2126 * channelLuminance(rgb.r) +
    0.7152 * channelLuminance(rgb.g) +
    0.0722 * channelLuminance(rgb.b)
  );
}

/** WCAG 2.1 contrast ratio between two colors, 1..21. */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Pick whichever ink reads better on `fill`.
 *
 * Translucent fills are NOT handled — an `rgba()` fill composites against
 * whatever is behind it, which this function cannot see. Alpha is ignored and
 * the raw channels are used, so callers with translucent backgrounds should
 * pass the effective composited color (or use a solid fill instead).
 *
 * Unparseable input falls back to `ON_DARK_INK`, preserving today's behavior.
 */
export function onColor(fill: string): string {
  if (!parseColor(fill)) return ON_DARK_INK;
  return contrastRatio(fill, ON_DARK_INK) >= contrastRatio(fill, ON_LIGHT_INK)
    ? ON_DARK_INK
    : ON_LIGHT_INK;
}
